/**
 * Format a duration in seconds to a human-readable string
 * Shows hours, minutes, and seconds as needed
 * Examples:
 *   45 -> "45s"
 *   90 -> "1m 30s"
 *   3665 -> "1h 1m 5s"
 *   3600 -> "1h"
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "--";
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0 || (hours > 0 && secs > 0)) {
    parts.push(`${minutes}m`);
  }
  if (secs > 0 || (hours === 0 && minutes === 0)) {
    parts.push(`${secs}s`);
  }

  return parts.join(" ") || "0s";
}

/**
 * Format a duration in seconds to a compact string (MM:SS or HH:MM:SS)
 * Best for timer displays
 */
export function formatDurationCompact(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "--:--";
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

// Tests
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest;

  describe("formatDuration", () => {
    it("formats seconds only", () => {
      expect(formatDuration(45)).toBe("45s");
      expect(formatDuration(0)).toBe("0s");
    });

    it("formats minutes and seconds", () => {
      expect(formatDuration(90)).toBe("1m 30s");
      expect(formatDuration(60)).toBe("1m");
      expect(formatDuration(59)).toBe("59s");
    });

    it("formats hours, minutes, and seconds", () => {
      expect(formatDuration(3665)).toBe("1h 1m 5s");
      expect(formatDuration(3600)).toBe("1h");
      expect(formatDuration(7200)).toBe("2h");
    });

    it("handles edge cases", () => {
      expect(formatDuration(-1)).toBe("--");
      expect(formatDuration(NaN)).toBe("--");
      expect(formatDuration(Infinity)).toBe("--");
    });
  });

  describe("formatDurationCompact", () => {
    it("formats MM:SS", () => {
      expect(formatDurationCompact(45)).toBe("00:45");
      expect(formatDurationCompact(90)).toBe("01:30");
      expect(formatDurationCompact(59)).toBe("00:59");
    });

    it("formats HH:MM:SS", () => {
      expect(formatDurationCompact(3665)).toBe("01:01:05");
      expect(formatDurationCompact(3600)).toBe("01:00:00");
    });

    it("handles edge cases", () => {
      expect(formatDurationCompact(-1)).toBe("--:--");
      expect(formatDurationCompact(NaN)).toBe("--:--");
    });
  });
}
